import React, { useContext, useEffect, useRef, useState, memo } from 'react'
import axios from '../Common/axios';
import { getCompanyConfig } from '../Common/api';
import AppContext from '../../components/AppContext';
import { Form, Input, InputNumber, Modal, Select, DatePicker } from "antd";
import { getNowDate, isEmpty } from './util';
import { scoreSelectOptions, resultCdSelectOptions, gradeSelectOptions, applyActionSelectOptions, applyTimeSelectOptions, applyTimeDays, applyTimeRanges, ynSelectOptions, osSelectOptions} from '../Common/options';
// import SelectBox from './SelectBox';
import { fieldRules } from './validationObj';
import dayjs from 'dayjs';

const { TextArea } = Input;
const xlsx = require("xlsx");
const { Option } = Select;
const nbsp = (<>&nbsp;</>);
/**
 * @description
 * excel download
 */
export const excelDownload = (title, data, excludedCol) => {

    if (isEmpty(data)) {
        Modal.info({ title: "다운로드할 데이타가 없습니다.", okText: "Close" });
        return;
    }

    let excludedColumns = ['id', 'sys_reg_user', 'sys_reg_date', 'sys_mod_user', 'sys_mod_date'];
    if (!isEmpty(excludedCol)) {
        excludedColumns = excludedColumns.concat(excludedCol);
    }

    const filteredData = data.map(item => {
        // 원하는 칼럼을 제외한 다른 데이터만 필터링
        const filteredItem = {};
        for (const key in item) {
            if (!excludedColumns.includes(key)) {
                filteredItem[key] = item[key];
            }
        }
        return filteredItem;
    });

    const ws = xlsx.utils.json_to_sheet(filteredData);
    const wb = xlsx.utils.book_new();

    xlsx.utils.book_append_sheet(wb, ws, title);
    xlsx.writeFile(wb, `${title}_${getNowDate()}.xlsx`);
};

export const csvDownload = (title, data, excludedCol) => {

    let excludedColumns = [];

    if (isEmpty(data)) {
        Modal.info({ title: "다운로드할 데이타가 없습니다.", okText: "Close" });
        return;
    }

    if (title == '자산목록') {
        excludedColumns = ['id', 'sys_reg_user', 'sys_reg_date', 'sys_mod_user', 'sys_mod_date','report_id'];
    }else{
        excludedColumns = ['id', 'sys_reg_user', 'sys_reg_date', 'sys_mod_user', 'sys_mod_date','report_id', 'use_yn'];
    }

    const headers = Object.keys(data[0]).filter(key => !excludedColumns.includes(key)).join(",");
    const filteredData = data.map(item => {
        // 원하는 칼럼을 제외한 다른 데이터만 필터링
        const filteredItem = [];
        for (const key in item) {
            if (!excludedColumns.includes(key)) {
                let value = item[key]?.toString() || '';
                if (value.includes(',') || value.includes('"') || value.includes('\\n')) {
                    value = `"${value}"`;
                }
                filteredItem.push(value);
            }            
        }
        return filteredItem.join(",");
    }).join("\n");

    const csvData = headers+"\n"+filteredData
    // BOM 추가 (한글 깨짐 방지)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);

    // 다운로드 링크 생성 및 클릭
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', title+'_템플릿.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function convertNewlines(obj) {
    let str = obj[1];
    if(typeof str === 'string'){
        str = str.replace(/\\\\n/g, '<br/>');
        str = str.replace(/\\n/g, '<br/>');
    }
    return str;
}

const EditableContext = React.createContext(null);

export const EditableRow = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

export const EditableCell = memo(({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    onAssetTypeSelectOptions,
    ...restProps
}) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);

    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const tabToggleEdit = (e) => {
        e.preventDefault();
        toggleEdit();
    };

    const save = async (e) => {        
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({
                ...record,
                ...values,
            });
        } catch (errInfo) {
            toggleEdit();
            if (errInfo.errorFields && errInfo.errorFields.length > 0) {
                const firstErrorField = errInfo.errorFields[0].name;
                form.scrollToField(firstErrorField);
                form.getFieldInstance(firstErrorField[0]).focus();
            }
        }
    };

    const disabledDate = (current) => {
        // apply_time에 따른 날짜 범위 제한
        const timeType = record["apply_time"];
        const baseDate = record.sys_reg_date ? dayjs(record.sys_reg_date) : dayjs();
        const range = applyTimeRanges[timeType];
        
        if (!range) return false; // apply_time이 없으면 제한 없음
        
        // 기준일로부터 min일 ~ max일 사이만 선택 가능
        const minDate = baseDate.add(range.min, 'day');
        const maxDate = baseDate.add(range.max, 'day');
        return current && (current < minDate.startOf('day') || current > maxDate.endOf('day'));
    };

    let childNode = children;
    let defaultAlign = 'center';
    let inputNode;
    if (editable) {
        
        if (dataIndex == 'confidential_score' || dataIndex == 'integrity_score' || dataIndex == 'availability_score') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {scoreSelectOptions.length > 0 &&
                    scoreSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value}>{item.text}</Option>
                        )
                    })}
            </Select>;
        } else if (dataIndex == 'strength_grd' || dataIndex == 'frequency_grd') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {gradeSelectOptions.length > 0 &&
                    gradeSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value} style={{ textAlign: 'center' }}>{item.value == item.text ? '' : '[' + item.value + ']'} {item.text}</Option>
                        )
                    })}
            </Select>;
        } else if (dataIndex == 'result_cd') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {resultCdSelectOptions.length > 0 &&
                    resultCdSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value} style={{ textAlign: 'center' }}>{item.value == item.text ? '' : '[' + item.value + ']'} {item.text}</Option>
                        )
                    })}
            </Select>;
        } else if (dataIndex == 'apply_action') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {applyActionSelectOptions.map((item, i) => {
                    if (i === 0) return null;
                    return (
                        <Option key={i} value={item.value} style={{ textAlign: 'center' }}>{item.value == item.text ? '' : '[' + item.value + ']'} {item.text}</Option>
                    )
                })}
            </Select>;
        } else if (dataIndex == 'apply_time') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} allowClear 
                onChange={(value) => {
                    // apply_time 변경 시 action_time 자동 설정
                    let newActionTime = '';
                    const baseDate = record.sys_reg_date ? new Date(record.sys_reg_date) : new Date();
                    
                    if (applyTimeDays[value]) {
                        const targetDate = new Date(baseDate);
                        targetDate.setDate(targetDate.getDate() + applyTimeDays[value]);
                        newActionTime = targetDate.toISOString().split('T')[0];
                    }
                    
                    // action_time도 함께 업데이트
                    form.setFieldsValue({
                        [dataIndex]: value,
                        action_time: newActionTime
                    });
                    
                    // handleSave를 통해 전체 레코드 업데이트
                    handleSave({
                        ...record,
                        apply_time: value,
                        action_time: newActionTime
                    });
                }} >
                {applyTimeSelectOptions.map((item, i) => {
                    return (
                        <Option key={i} value={item.value} style={{ textAlign: 'center' }}>{item.value == item.text ? '' : '[' + item.value + ']'} {item.text}</Option>
                    )
                })}
            </Select>;
        } else if (dataIndex.indexOf('_score') > -1) {
            inputNode = <InputNumber ref={inputRef} min={1} max={3} onPressEnter={save} onBlur={save} size={'small'} style={{ width: '60px' }} />;
        } else if (dataIndex.indexOf('_measure') > -1) {
            inputNode = <InputNumber ref={inputRef} onPressEnter={save} onBlur={save} size={'small'} style={{ width: '60px' }} />;
        } else if (dataIndex.indexOf('_desc') > -1) {
            // corrective_desc는 1줄로 시작, 제한 없이 확장
            if (dataIndex === 'corrective_desc') {
                inputNode = <TextArea rows={1} ref={inputRef} onBlur={save} autoSize={{ minRows: 1 }} />;
            } else {
                inputNode = <TextArea rows={4} ref={inputRef} onBlur={save} autoSize={{ minRows: 2, maxRows: 6 }} />;
            }
        } else if (dataIndex.indexOf('remark') > -1) {
            inputNode = <TextArea 
                rows={1} 
                autoSize={{ minRows: 1, maxRows: 6 }} 
                style={{ 
                    width: "100%", 
                    resize: 'none',
                    padding: '4px 11px',
                    lineHeight: '1.5715'
                }} 
                ref={inputRef} 
                onBlur={save} 
            />;
        } else if (dataIndex == 'isms_yn' || dataIndex == 'rms_yn' || dataIndex == 'use_yn') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {ynSelectOptions.length > 0 &&
                    ynSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value} style={{ textAlign: 'center' }}>{item.value == item.text ? '' : '[' + item.value + ']'} {item.text}</Option>
                        )
                    })}
            </Select>;
        } else if (dataIndex == 'asset_spec') {
            if(onAssetTypeSelectOptions){
                inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                    {onAssetTypeSelectOptions.length > 0 &&
                        onAssetTypeSelectOptions.filter((item) => item.type == record.asset_type).map((item, i) => {
                            return (
                                
                                <Option key={i} value={item.value} title={item.text}> 
                                    <span className="block truncate">{item.text}</span>
                                </Option>
                            )
                        })}
                </Select>;
            }else{
                inputNode = <Input ref={inputRef} onPressEnter={save} onBlur={save} />;
            }
        } else if (dataIndex == 'os_type') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true} onBlur={save} onChange={save} >
                {osSelectOptions.length > 0 &&
                    osSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value}> {item.text}</Option>
                        )
                    })}
            </Select>;
        } else if (dataIndex == 'result_desc'){
            defaultAlign = 'left';
        } else if (dataIndex == 'action_time') {
            inputNode = (
                <>
                <DatePicker
                    ref={inputRef}
                    style={{ width: '110%' }}
                    format="YYYY-MM-DD"
                    value={record[dataIndex] ? dayjs(record[dataIndex]) : null}
                    onChange={(record, dateString) => {
                        console.log("dateString", dateString);
                        console.log("record", disabledDate);
                        form.setFieldsValue({
                            [dataIndex]: dateString,
                        });
                        setTimeout(save, 0);
                    }}
                    disabledDate={disabledDate}
                    autoFocus={true}
                    onBlur={save}
                    open={editing}
                />
                </> 
            );
        } else if (dataIndex == '') {
            inputNode = <Select ref={inputRef} defaultOpen={true} autoFocus={true}  >
                {osSelectOptions.length > 0 &&
                    osSelectOptions.map((item, i) => {
                        return (
                            <Option key={i} value={item.value}> {item.text}</Option>
                        )
                    })}
            </Select>;
        } else {
            inputNode = <Input ref={inputRef} onPressEnter={save} onBlur={save} />;
        }
        
        childNode = editing ? (
            <Form.Item
                style={{ 
                    margin: 0,
                    width: '100%'
                }}
                name={dataIndex}
                // name={`${record._uniqueId ? record._uniqueId : "id"}_${dataIndex}`}
                rules={fieldRules[dataIndex]}
            >
                {inputNode}
            </Form.Item>
        ) : (
            <div
                style={{
                    width: '100%',
                    padding: '4px 11px',
                    minHeight: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: defaultAlign === 'left' ? 'flex-start' : defaultAlign === 'right' ? 'flex-end' : 'center'
                }}
                onClick={toggleEdit}
                onFocus={tabToggleEdit}
                tabIndex={0}
            >
               {children}
            </div>
        );
        
    }
    
    return <td {...restProps}>{childNode}</td>;
});

const updateCompanyConfig = async (res, myContext) => {
    
    myContext.setAssetGradeHigh(res.val_01);
    myContext.setAssetGradeRow(res.val_02);
    
    myContext.setRiskGrdHigh(res.val_03);
    myContext.setRiskGrdRow(res.val_04);

    myContext.setDoa(res.val_10);
    myContext.setCompanyCode(res.code_cd);
    myContext.setCompanyName(res.code_nm);   
    // if(res.val_07){ }
};

export const companyEnvInit = async(pCompanycode, myContext)=> {
        try {
            const response = await axios.get(getCompanyConfig(pCompanycode))
            await updateCompanyConfig(response.data, myContext);
        } catch (error) {
            console.error("function Company config fetch error:", error);
            return false;
        }
};